import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { FileUp, Printer, FileText, ClipboardPaste } from "lucide-react";
import api from "../Axios/api";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import JsBarcode from "jsbarcode";
import Cookies from "js-cookie";
import Loader from "../common/Loader";
const ReportScreen = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [searchText, setSearchText] = useState("");
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await api.get(
        `/receipt-note/computer-parts-list?month=${month}`,
      );
      const sortedDesc = res.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
      // console.log(sortedDesc);
      setData(sortedDesc);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month]);

  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const monthNum = String(now.getMonth() + 1).padStart(2, "0");
    setMonth(`${year}-${monthNum}`);
  }, []);

  const handleGrnPrint = (cell) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `https://purchase24.polybonderps.site/purchase-print/receipt-print.php`;
    form.target = "_blank";

    const receiptId = document.createElement("input");
    receiptId.type = "hidden";
    receiptId.name = "receiptId";
    receiptId.value = cell;
    form.appendChild(receiptId);

    const dataInput = document.createElement("input");
    dataInput.type = "hidden";
    dataInput.name = "token";
    dataInput.value = JSON.stringify(Cookies.get("access_token"));
    form.appendChild(dataInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const groupedData = Object.values(
    data.reduce((acc, curr) => {
      const key = `${curr.bill_no}-${curr.grn_no}`;
      if (!acc[key]) {
        acc[key] = {
          bill_no: curr.bill_no,
          date: curr.date,
          grn_no: curr.grn_no,
          grn_id: curr.receipt_item_Info[0].receipt_note_id,
          items: curr.receipt_item_Info || [],
          total_amount: curr.total_amount || 0,
        };
      } else {
        acc[key].items.push(...(curr.receipt_item_Info || []));
        acc[key].total_amount += curr.total_amount || 0;
      }
      return acc;
    }, {}),
  );

  const filteredData = groupedData.filter((group) => {
    const text = searchText.trim().toLowerCase();
    if (!text) return true;

    const billOrGrnMatch =
      group.bill_no?.toLowerCase().includes(text) ||
      group.grn_no?.toLowerCase().includes(text);

    const itemMatch = group.items.some((item) => {
      const title = item.item_title?.toLowerCase() || "";

      const users = Array.isArray(item.item_user)
        ? item.item_user.join(", ").toLowerCase()
        : String(item.item_user || "").toLowerCase();

      const areas = Array.isArray(item.item_area)
        ? item.item_area.join(", ").toLowerCase()
        : String(item.item_area || "").toLowerCase();

      return (
        title.includes(text) || users.includes(text) || areas.includes(text)
      );
    });

    return billOrGrnMatch || itemMatch;
  });

  const groupedArray = Object.values(groupedData);

  const columns = [
    {
      name: "S.No",
      selector: (_, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Date",
      selector: (row) => new Date(row.date).toLocaleDateString("en-GB"),
      sortable: true,
    },
    { name: "Bill No", selector: (row) => row.bill_no, sortable: true },
    { name: "GRN No", selector: (row) => row.grn_no, sortable: true },
    {
      name: "Total Amount",
      selector: (row) => {
        const total = row.items.reduce((sum, i) => {
          const qty = Number(i.receipt_item_qty) || 0;
          const rate = Number(i.receipt_item_rate) || 0;
          const gst = Number(i.receipt_item_gst) || 0;

          const lineTotal = qty * rate * (1 + gst / 100);
          return sum + lineTotal;
        }, 0);

        return Math.round(total);
      },
      sortable: true,
      right: true,
    },
    {
      name: "No. of Items",
      selector: (row) => row.items.length,
      sortable: true,
      right: true,
    },
    {
      name: "Action",
      selector: (row) => row.grn_id,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className="flex items-center gap-3">
          <button onClick={() => handleGrnPrint(row.grn_id)}>
            <FileText size={20} />
          </button>
        </div>
      ),
    },
  ];
  const customStyles = {
    headCells: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#e2e2e2",
        color: "#374151",
        borderRight: "1px solid #ddd",
        padding: "6px 8px",
      },
    },
  };
  const handlePrint = (data, grn_no) => {
    /* ---------- FINANCIAL YEAR ---------- */
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    const fyStart = month >= 4 ? year : year - 1;
    const fyEnd = fyStart + 1;
    const financialYear = `${fyStart}-${String(fyEnd).slice(-2)}`;

    /* ---------- SAFE DATA EXTRACTION ---------- */
    const grn = String(grn_no || "000").trim();
    const shortTitle = String(data?.item_title || "")
      .trim()
      .substring(0, 4);

    const title = `POLY/IT/${financialYear}/${grn}/${shortTitle}`;

    /* ---------- CANVAS SETUP ---------- */
    const scale = 2;
    const width = 150;
    const height = 100;

    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;

    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    /* ---------- LOAD LOGO ---------- */
    const logo = new Image();
    logo.src = "/polybond.png";

    logo.onload = () => {
      /* ---------- LOGO ---------- */
      ctx.drawImage(logo, (width - 60) / 2, 10, 60, 20);

      /* ---------- TITLE ---------- */
      ctx.fillStyle = "#000";
      ctx.font = "bold 10px Arial";
      ctx.fillText(title, width / 2, 45);

      /* ---------- BARCODE ---------- */
      if (!title) return;

      const barcodeCanvas = document.createElement("canvas");

      JsBarcode(barcodeCanvas, grn, {
        format: "CODE128B",
        width: 4,
        height: 35,
        displayValue: false,
        margin: 5,
      });

      ctx.drawImage(barcodeCanvas, (width - 120) / 2, 55, 120, 30);

      /* ---------- DOWNLOAD ---------- */
      const link = document.createElement("a");
      link.download = `IT-${Date.now()}.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.click();
    };
  };

  const ExpandedComponent = ({ data }) => (
    <div className="p-4 bg-gray-50">
      <table className="w-full text-xs border border-gray-300 border-collapse">
        <thead className="bg-gray-200">
          <tr>
            <th className="border px-2 py-1">Item</th>
            <th className="border px-2 py-1">Rate</th>
            <th className="border px-2 py-1">Qty</th>
            <th className="border px-2 py-1">Amount (with GST)</th>
            <th className="border px-2 py-1">User</th>
            <th className="border px-2 py-1">Area</th>
            <th className="border px-2 py-1">IT Spare Qty</th>
            <th className="border px-2 py-1">Serial No.</th>
            <th className="border px-2 py-1">Status</th>
            <th className="border px-2 py-1">Warranty</th>
            <th className="border px-2 py-1">Expiry Date</th>
            <th className="border px-2 py-1">Remark</th>
            <th className="border px-2 py-1">Sticker</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, idx) => (
            <tr key={idx}>
              <td className="border px-2 py-1 text-center align-middle">
                {item.item_title}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                ₹{item.receipt_item_rate}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.receipt_item_qty}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                ₹
                {Math.round(
                  (Number(item.receipt_item_qty) || 0) *
                    (Number(item.receipt_item_rate) || 0) *
                    (1 + (Number(item.receipt_item_gst) || 0) / 100),
                )}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {Array.isArray(item.item_user)
                  ? item.item_user.join(", ")
                  : item.item_user || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.item_area || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.spareQty || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.serialNumber || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.status || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.warranty || " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.warrantyExpiry
                  ? new Date(item.warrantyExpiry).toLocaleDateString("en-IN")
                  : " "}
              </td>
              <td className="border px-2 py-1 text-center align-middle">
                {item.remark || " "}
              </td>

              <td className="border px-2 py-1 text-center align-middle">
                <button
                  type="button"
                  className="flex items-center justify-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm mx-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrint(item, data.grn_no);
                  }}
                >
                  <Printer size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const exportToExcel = async () => {
    if (!groupedArray || !groupedArray.length) return;

    const dateStr = groupedArray?.[0]?.date;
    const d = dateStr ? new Date(dateStr) : null;
    const currentMonth = d
      ? d.toLocaleString("en-US", { month: "short", year: "numeric" })
      : "";

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Invoice Report");

    // 👉 Top Header
    sheet.mergeCells(1, 1, 1, 12);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = `ODYSSEY COMPUTER - ${currentMonth} SUMMARY (IT PURCHASE ITEMS)`;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    // 👉 Column Header
    const headerRow = sheet.addRow([
      "S.No",
      "Date",
      "Bill No",
      "GRN No",
      "Item Title",
      "Qty",
      "Rate",
      "Total (with GST)",
      "User",
      "Area",
      "Status",
      "Remark",
    ]);

    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle" };

    // 👉 Column widths
    sheet.columns = [
      { width: 6 },
      { width: 12 },
      { width: 10 },
      { width: 10 },
      { width: 25 },
      { width: 8 },
      { width: 10 },
      { width: 15 },
      { width: 25 },
      { width: 15 },
      { width: 15 },
      { width: 30 },
    ];

    let grandTotal = 0;
    let currentRow = 3; // data starts after title + header
    let billSerial = 1;
    groupedArray.forEach((invoice) => {
      const items = invoice.items || [];
      if (!items.length) return;

      const startRow = currentRow;

      items.forEach((item, idx) => {
        const totalWithGST =
          Number(item.receipt_item_total_with_gst) ||
          Math.round(
            (Number(item.receipt_item_qty) || 0) *
              (Number(item.receipt_item_rate) || 0) *
              (1 + (Number(item.receipt_item_gst) || 0) / 100),
          );

        grandTotal += totalWithGST;

        sheet.addRow([
          idx === 0 ? billSerial : "", // S.No per bill
          idx === 0
            ? invoice.date
              ? new Date(invoice.date).toLocaleDateString("en-GB")
              : ""
            : "",
          idx === 0 ? invoice.bill_no || "" : "",
          idx === 0 ? invoice.grn_no || "" : "",
          item.item_title || "",
          item.receipt_item_qty || 0,
          item.receipt_item_rate || 0,
          Math.round(totalWithGST),
          Array.isArray(item.item_user)
            ? item.item_user.join(", ")
            : item.item_user || "---",
          item.item_area || "---",
          item.status || "---",
          item.remark || "---",
        ]);

        currentRow++;
      });

      const endRow = currentRow - 1;

      // 👉 Vertical merge for invoice block
      if (items.length > 1) {
        sheet.mergeCells(startRow, 1, endRow, 1); // S.No
        sheet.mergeCells(startRow, 2, endRow, 2); // Date
        sheet.mergeCells(startRow, 3, endRow, 3); // Bill
        sheet.mergeCells(startRow, 4, endRow, 4); // GRN
      }
      billSerial++;
    });

    // 👉 Grand total
    const totalRow = sheet.addRow([
      "GRAND TOTAL",
      "",
      "",
      "",
      "",
      "",
      "",
      Math.round(grandTotal),
      "",
      "",
      "",
      "",
    ]);

    sheet.mergeCells(totalRow.number, 1, totalRow.number, 7);
    totalRow.font = { bold: true };

    // 👉 Export file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Invoice-Report.xlsx";
    link.click();
  };
  return (
    <div className="min-h-screen p-6 bg-gray-100">
      <div className="max-w-8xl mx-auto bg-white rounded-xl shadow-lg p-6 border border-gray-300">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800 mb-3 md:mb-0">
            Summary Invoice Report
          </h1>
          {groupedArray.length > 0 && (
            <div className="text-right mb-4">
              <h2 className="text-medium font-semibold text-blue-600">
                Grand Total : ₹{" "}
                {Math.round(
                  groupedArray.reduce((sum, inv) => {
                    const invoiceTotal = inv.items.reduce((s, i) => {
                      const qty = Number(i.receipt_item_qty) || 0;
                      const rate = Number(i.receipt_item_rate) || 0;
                      const gst = Number(i.receipt_item_gst) || 0;

                      const lineTotal = qty * rate * (1 + gst / 100);
                      return s + lineTotal;
                    }, 0);

                    return sum + invoiceTotal;
                  }, 0),
                )}
              </h2>
            </div>
          )}

          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 hover:border-blue-400 transition-all duration-200 w-full md:w-auto"
          />
        </div>
        <div className="flex items-center justify-between gap-10 px-2 mt-2 mb-6">
          {/* Export Button */}
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-lg shadow-md hover:scale-105 hover:shadow-lg transition-all duration-300"
            onClick={exportToExcel}
          >
            <FileUp className="w-5 h-5" />
            <span className="text-sm font-medium">Export Excel</span>
          </button>

          <button
            className="flex items-center gap-2 px-4 py-2  rounded-lg shadow-md hover:scale-105 hover:shadow-lg transition-all duration-300"
            onClick={() => window.open("/computer-spare-list", "_blank")}
          >
            <ClipboardPaste className="w-5 h-5" />
            <span className="text-sm font-medium">IT Spare</span>
          </button>

          {/* Search Box */}
          <div className="relative group">
            <input
              type="text"
              placeholder="Search ..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-40 g  focus:w-64 px-4 py-2 border border-gray-300 rounded-full outline-none transition-all duration-300 ease-in-out shadow-sm focus:shadow-md focus:border-blue-400"
            />
            <span className="absolute right-3 top-2.5 text-gray-400 group-hover:text-blue-500 transition-colors duration-300">
              🔍
            </span>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <DataTable
            columns={columns}
            data={searchText.trim() ? filteredData : groupedData}
            progressPending={isLoading}
            pagination
            highlightOnHover
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            persistTableHead
            customStyles={customStyles}
          />
        )}
      </div>
    </div>
  );
};

export default ReportScreen;
